// 장비 장착 피드백 모델(순수 로직, 2026-10-28 대표님 「정확하게 바뀌었는지 안 바뀌었는지 인지가 안 돼서 헤맨다」).
// 장착이 일어나면 ① 가장자리 토스트 "무기 장착: 이름 공격 +N" ② 칸 번쩍임·「장착됨」 배지 ③ 퀘스트 줄 체크가 함께 나오도록 필요한 값만 계산한다.
// DOM·three를 모른다. 객체는 장착이 일어날 때만 만든다(프레임 루프에서는 비교만).

export interface TotalsLite { attack: number; def: number; maxHp: number }

/** 능력치 변화 한 줄: 공격 +12 ▲ / 방어 -3 ▼ */
export interface DeltaPart { readonly label: string; readonly value: number; readonly text: string; readonly up: boolean }

const STAT_PARTS: readonly (readonly [keyof TotalsLite, string])[] = [['attack', '공격'], ['def', '방어'], ['maxHp', 'HP']];

export function fmtSigned(n: number): string {
  const r = Math.round(n);
  return (r >= 0 ? '+' : '-') + Math.abs(r).toLocaleString('en-US');
}

/** 장착 전후 합계 차이(반올림해서 0이면 뺀다) */
export function equipDelta(prev: TotalsLite, next: TotalsLite): DeltaPart[] {
  const out: DeltaPart[] = [];
  for (const [k, label] of STAT_PARTS) {
    const d = Math.round(next[k]) - Math.round(prev[k]);
    if (d === 0) continue;
    out.push({ label, value: d, text: label + ' ' + fmtSigned(d), up: d > 0 });
  }
  return out;
}

/** 장착 칸 번호(SLOT_ORDER 순서) 중 uid가 바뀌고 지금 비어 있지 않은 것 */
export function newlyEquipped(prev: readonly (string | null)[], now: readonly (string | null)[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < now.length; i++) if (now[i] && now[i] !== (prev[i] ?? null)) out.push(i);
  return out;
}

export interface EquipToastText {
  /** "무기 장착: 낡은 검" · "3개 장착:" */
  readonly head: string;
  readonly parts: readonly DeltaPart[];
  /** 변화가 없을 때 쓰는 말(parts가 비었을 때) */
  readonly none: string;
}

/** 한 개면 "<부위> 장착: <이름> 공격 +N", 여러 개면 "N개 장착: 공격 +12, 방어 +5" */
export function equipToast(items: readonly { slot: string; name: string }[], parts: readonly DeltaPart[]): EquipToastText {
  const head = items.length === 1 ? items[0].slot + ' 장착: ' + items[0].name : items.length + '개 장착:';
  return { head, parts, none: '능력치는 그대로' };
}
/** 시험·접근성용 한 줄 글자 */
export function equipToastPlain(t: EquipToastText): string {
  const body = t.parts.length ? t.parts.map((p) => p.text + (p.up ? ' ▲' : ' ▼')).join(', ') : t.none;
  return t.head + ' ' + body;
}

/** 화면 시간(ms) */
export const EQUIP_FX = { toastMs: 2800, markMs: 2400, tickMs: 1700 };
/** QA(?qa&eqfx=배수): 소프트웨어 렌더처럼 화면 캡처가 느린 환경에서 표시 시간을 늘려 찍는다. 정상 플레이에는 쓰지 않는다 */
export function scaleEquipFx(k: number): void {
  if (!Number.isFinite(k) || k <= 1) return;
  EQUIP_FX.toastMs = Math.round(2800 * k); EQUIP_FX.markMs = Math.round(2400 * k); EQUIP_FX.tickMs = Math.round(1700 * k);
  if (typeof document !== 'undefined') document.documentElement.style.setProperty('--dh-eq-k', String(k));
}

/** 방금 장착한 장비 uid → 사라지는 시각. 가방 칸·상세 카드가 번쩍임과 「장착됨」 배지에 쓴다(CSS 애니메이션이 스스로 사라지므로 시각은 다시 그릴 때만 본다) */
class EquipMarks {
  private readonly until = new Map<string, number>();
  mark(uids: readonly string[], now: number): void { for (const u of uids) this.until.set(u, now + EQUIP_FX.markMs); }
  recent(uid: string, now: number): boolean {
    const t = this.until.get(uid);
    if (t === undefined) return false;
    if (now >= t) { this.until.delete(uid); return false; }
    return true;
  }
  /** 남은 표시 시간(ms), 없으면 0 */
  remain(uid: string, now: number): number { return this.recent(uid, now) ? (this.until.get(uid) as number) - now : 0; }
  clear(): void { this.until.clear(); }
}
export const equipMarks = new EquipMarks();

/** 퀘스트 줄의 「✔ 장착 완료」 표시(다음 목표로 넘어가기 전 잠깐 보여 준다) */
export const equipTick = { until: 0, name: '', detail: '' };

/** 지금 한 번에 장착할 추천 장비 수(성장 목표가 채운다). 장비창 머리말 버튼이 읽는다 */
export const equipHints = { count: 0 };

/** 「추천 장비 모두 장착」 신호: 장비창·퀘스트 줄이 보내고 EquipFeedback이 받아 명령을 낸다 */
export const EQUIP_BEST_EVENT = 'dh-equip-best';

/** 장착 알림 판정 한 번의 결과: 새로 장착된 칸 번호와 장착 전 합계(토스트 차이 계산용). 알릴 것이 없으면 null */
export interface EquipChange { readonly idx: readonly number[]; readonly prev: TotalsLite }

/**
 * 프레임마다 장착 칸을 비교해 「방금 장착했다」를 판정한다(DOM 없음, 시험 가능).
 * - 처음 프레임과 영웅 전환 프레임은 기준만 잡고 알리지 않는다(영웅마다 무기 칸·합계가 달라 전환이 장착처럼 보이던 29차 QA 오표시).
 * - 새 명령 결과(last.seq 증가)가 장착 성공일 때만 알린다. 칸만 바뀌고 결과가 그대로면(영웅 전환·다른 탭·불러오기) 지난 결과를 다시 알리지 않는다.
 * 같은 프레임 비교는 할당하지 않는다(알릴 때만 배열을 만든다).
 */
export class EquipChangeTracker {
  private ready = false;
  private hero = '';
  private seq = -1;
  private readonly snap: (string | null)[] = [];
  private readonly prev: TotalsLite = { attack: 0, def: 0, maxHp: 0 };

  observe(hero: string, last: { readonly seq: number; readonly kind: string; readonly code: string },
    slots: readonly string[], equipped: Readonly<Record<string, string | null>>, totals: TotalsLite): EquipChange | null {
    let out: EquipChange | null = null;
    if (this.ready && hero === this.hero && last.seq !== this.seq && last.kind === 'equip' && last.code === 'ok') {
      let idx: number[] | null = null;
      for (let i = 0; i < slots.length; i++) {
        const now = equipped[slots[i]] ?? null;
        if (now && now !== (this.snap[i] ?? null)) (idx ??= []).push(i);
      }
      if (idx) out = { idx, prev: { attack: this.prev.attack, def: this.prev.def, maxHp: this.prev.maxHp } };
    }
    this.ready = true; this.hero = hero; this.seq = last.seq;
    for (let i = 0; i < slots.length; i++) this.snap[i] = equipped[slots[i]] ?? null;
    this.prev.attack = totals.attack; this.prev.def = totals.def; this.prev.maxHp = totals.maxHp;
    return out;
  }
}
